import { useQuery } from "@tanstack/react-query";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  formatInvoicePeriod,
  formatPlanDate,
} from "@/components/billing-plan/billing-plan-values";
import {
  ORG_WAGE_PASS_THROUGH_DETAIL,
  SAILE_CHARGES_LABEL,
  WAGE_PASS_THROUGH_LABEL,
} from "@/components/billing-plan/wage-pass-through-copy";
import { useTRPC } from "@/trpc";
import {
  ESTIMATED_TOTAL_LABEL,
  NOT_BILLED_LABEL,
  NO_PLAN_DESCRIPTION,
  NO_USAGE_NOTICE,
  OVER_INCLUDED_LABEL,
  REFERENCE_TOTAL_LABEL,
  REPORTING_ONLY_BODY,
  REPORTING_ONLY_TITLE,
  TABLE_CAPTION,
  USAGE_LOAD_ERROR,
  buildUsageRows,
  buildUsageTotals,
  describeCycleEstimate,
  describeTimingNote,
  formatUpdatedAt,
  type CurrentCycleUsage,
  type UsageRow,
  type UsageTotals,
} from "./usage-presentation";

const CARD_TITLE = "Usage this billing cycle";
const PERCENT_MAX = 100;

/** Decorative: the adjacent text carries the meaning. */
function AllowanceMeter({
  ratio,
  className,
}: {
  ratio: number;
  className: string;
}) {
  return (
    <div
      aria-hidden="true"
      className={`h-1.5 rounded-full bg-muted ${className}`}
    >
      <div
        className="h-full rounded-full bg-primary"
        style={{ width: `${ratio * PERCENT_MAX}%` }}
      />
    </div>
  );
}

function RateCell({ row }: { row: UsageRow }) {
  if (!row.rate) {
    return null;
  }
  return (
    <div className="flex flex-col items-start gap-1">
      {row.rate.isNotBilled ? (
        <Badge variant="outline">{NOT_BILLED_LABEL}</Badge>
      ) : (
        <span>{row.rate.summary}</span>
      )}
      {row.rate.isOverage && (
        <Badge variant="outline">{OVER_INCLUDED_LABEL}</Badge>
      )}
      {row.rate.note && (
        <span className="text-xs text-muted-foreground">{row.rate.note}</span>
      )}
      {row.meterRatio !== null && (
        <AllowanceMeter
          ratio={row.meterRatio}
          className="mt-1 w-full max-w-[8rem]"
        />
      )}
    </div>
  );
}

function UsageTable({
  rows,
  totalLabel,
  totals,
}: {
  rows: UsageRow[];
  totalLabel: string;
  totals: UsageTotals;
}) {
  return (
    <Table className="hidden md:table">
      <TableCaption className="sr-only">{TABLE_CAPTION}</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead>Item</TableHead>
          <TableHead>Used</TableHead>
          <TableHead>Included / rate</TableHead>
          <TableHead className="text-right">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key}>
            <TableCell>
              <p className="font-medium">{row.item}</p>
              {row.detail && (
                <p className="text-sm text-muted-foreground">{row.detail}</p>
              )}
            </TableCell>
            <TableCell>{row.used}</TableCell>
            <TableCell>
              <RateCell row={row} />
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {row.amount}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        {totals.wagePassThrough !== null && (
          <>
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground">
                {SAILE_CHARGES_LABEL}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {totals.saileCharges}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={3}>
                <p>{WAGE_PASS_THROUGH_LABEL}</p>
                <p className="text-sm font-normal text-muted-foreground">
                  {ORG_WAGE_PASS_THROUGH_DETAIL}
                </p>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {totals.wagePassThrough}
              </TableCell>
            </TableRow>
          </>
        )}
        <TableRow>
          <TableCell colSpan={3}>{totalLabel}</TableCell>
          <TableCell className="text-right font-semibold tabular-nums">
            {totals.total}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}

function UsageList({
  rows,
  totalLabel,
  totals,
}: {
  rows: UsageRow[];
  totalLabel: string;
  totals: UsageTotals;
}) {
  return (
    <ul className="divide-y md:hidden">
      {rows.map((row) => (
        <li key={row.key} className="space-y-2 py-3 first:pt-0">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-medium">{row.item}</p>
            <p className="text-right text-sm tabular-nums">{row.amount}</p>
          </div>
          <div className="space-y-1 text-sm text-muted-foreground">
            <p>{row.used}</p>
            {row.rate && (
              <div className="flex flex-wrap items-center gap-2">
                <span>{row.rate.summary}</span>
                {row.rate.isOverage && (
                  <Badge variant="outline">{OVER_INCLUDED_LABEL}</Badge>
                )}
              </div>
            )}
            {row.rate?.note && <p className="text-xs">{row.rate.note}</p>}
            {row.detail && <p>{row.detail}</p>}
          </div>
          {row.meterRatio !== null && (
            <AllowanceMeter ratio={row.meterRatio} className="w-full" />
          )}
        </li>
      ))}
      {totals.wagePassThrough !== null && (
        <>
          <li className="flex items-start justify-between gap-3 py-3">
            <p className="text-sm text-muted-foreground">
              {SAILE_CHARGES_LABEL}
            </p>
            <p className="text-right text-sm tabular-nums">
              {totals.saileCharges}
            </p>
          </li>
          <li className="space-y-1 py-3">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm">{WAGE_PASS_THROUGH_LABEL}</p>
              <p className="text-right text-sm tabular-nums">
                {totals.wagePassThrough}
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              {ORG_WAGE_PASS_THROUGH_DETAIL}
            </p>
          </li>
        </>
      )}
      <li className="flex items-start justify-between gap-3 py-3 last:pb-0">
        <p className="text-sm">{totalLabel}</p>
        <p className="text-right text-sm font-semibold tabular-nums">
          {totals.total}
        </p>
      </li>
    </ul>
  );
}

function UsageContent({ usage }: { usage: CurrentCycleUsage }) {
  const rows = buildUsageRows(usage);
  const hasCategories = usage.categories.length > 0;
  const totalLabel = usage.invoiced
    ? ESTIMATED_TOTAL_LABEL
    : REFERENCE_TOTAL_LABEL;
  const totals = buildUsageTotals(usage);

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        <time dateTime={usage.asOf}>{formatUpdatedAt(usage.asOf)}</time>
      </p>
      {!usage.invoiced && (
        <Alert role="note">
          <AlertTitle>{REPORTING_ONLY_TITLE}</AlertTitle>
          <AlertDescription>{REPORTING_ONLY_BODY}</AlertDescription>
        </Alert>
      )}
      <UsageTable rows={rows} totalLabel={totalLabel} totals={totals} />
      <UsageList rows={rows} totalLabel={totalLabel} totals={totals} />
      {!hasCategories && (
        <p className="text-sm text-muted-foreground">{NO_USAGE_NOTICE}</p>
      )}
      {usage.invoiced && (
        <p className="text-sm text-muted-foreground">
          {describeTimingNote({
            baseFeeTiming: usage.baseFeeTiming,
            renewalDateLabel: formatPlanDate(usage.cycle.renewalDate),
          })}
        </p>
      )}
    </div>
  );
}

export function UsageCard({
  organizationBusinessUnitId,
  unitName,
}: {
  organizationBusinessUnitId: string;
  unitName: string;
}) {
  const trpc = useTRPC();
  const usageQuery = useQuery(
    trpc.organization.getCurrentCycleUsage.queryOptions({
      organizationBusinessUnitId,
    }),
  );

  if (usageQuery.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{CARD_TITLE}</CardTitle>
        </CardHeader>
        <CardContent>
          <p role="alert" className="text-sm text-destructive">
            {USAGE_LOAD_ERROR}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (usageQuery.data === undefined) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{CARD_TITLE}</CardTitle>
        </CardHeader>
        <CardContent>
          <div role="status">
            <Skeleton className="h-40 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const usage = usageQuery.data;
  if (usage === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{CARD_TITLE}</CardTitle>
          <CardDescription>{NO_PLAN_DESCRIPTION}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No active billing plan for {unitName} right now, so there&apos;s no
            usage to show.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{CARD_TITLE}</CardTitle>
        <CardDescription>
          {describeCycleEstimate({
            period: formatInvoicePeriod({
              start: usage.cycle.periodStart,
              end: usage.cycle.periodEnd,
            }),
            unitName,
            invoiced: usage.invoiced,
          })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <UsageContent usage={usage} />
      </CardContent>
    </Card>
  );
}
